import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionInputHandle, ActionValidator } from '@bonusly/common-action-input';
import { useSharedConfig } from '@bonusly/common-give-box';
import { useAllowanceBoostId } from '@bonusly/common-rewards';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../../types/bonus';
import { useBudget } from '../../../components/comments/hooks/use-budget';
import { useCommentModules } from '../../../components/comments/hooks/use-comment-modules';
import { useInitialState } from '../../../components/comments/hooks/use-initial-state';
import { useUpdateComment } from '../../../components/comments/hooks/use-update-comment';
import { useSayThanks } from '../../../hooks/use-say-thanks';
import { parseErrorMessage } from '../../../utils/error';

interface UseCommentEditOptions {
	onError?: (error: unknown) => void;
	onSuccess?: VoidFunction;
}

export const useCommentEdit = (comment: Bonus, bonus: Bonus, options: UseCommentEditOptions = {}) => {
	const { t } = useTranslation('recognition');
	const inputRef = useRef<ActionInputHandle>(null);
	const user = useCurrentUser();
	const { id: allowanceBoostId } = useAllowanceBoostId();
	const handlers = useSharedConfig({ includeGroups: true });
	const modules = useCommentModules(bonus);
	const budget = useBudget();
	const isOwnRecognition = useSayThanks(bonus);
	const initialState = useInitialState(comment);

	const { isSubmitting, onSubmit } = useUpdateComment(comment, bonus, {
		onError: (err: unknown) => {
			const message = parseErrorMessage(err);
			inputRef.current?.error(message);

			options.onError?.(err);
		},
		onSuccess: () => {
			options.onSuccess?.();
			inputRef.current?.reset();
		},
	});

	const validator = useMemo(() => {
		const totalReceivers = comment.receivers.length + (comment.include_parent_giver ? 1 : 0);
		const totalAmount = comment.amount * totalReceivers;

		return new ActionValidator({
			requireAmount: false,
			requireRecipient: false,
			requireBalance: user.giving_balance + totalAmount,
			requirePositiveAmount: false,
		});
	}, [comment.amount, comment.include_parent_giver, comment.receivers, user]);

	const setInputText = (text: string) => {
		inputRef.current?.insert(text);
		inputRef.current?.focus();
	};

	return {
		allowanceBoostId,
		budget,
		context: 'comment',
		isOwnRecognition,
		isSubmitting,
		modules,
		placeholder: isOwnRecognition ? t('comment.input_thanks_placeholder') : t('comment.input_placeholder'),
		onSubmit,
		ref: inputRef,
		setInputText,
		validator,
		...handlers,
		...initialState,
	};
};
